/* eslint-disable jsx-a11y/interactive-supports-focus */
/* eslint-disable jsx-a11y/click-events-have-key-events */
import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import PropTypes from 'prop-types';
import { useSelector, shallowEqual } from 'react-redux';
import { Navbar, Nav, OverlayTrigger, Tooltip, Popover } from 'react-bootstrap';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import classNames from 'classnames';
import ebisLogo from 'assets/images/logo.svg';
import chatbotButtonImg from 'assets/images/chatbot_button_img.png';
import chatbotButtonImgActive from 'assets/images/chatbot_button_img_active.png';
import chatbotButtonStarActive from 'assets/images/chatbot_button_star_icon_active.png';
import capiSingleLogo from 'assets/images/logo-capi-single.svg';
import NotificationsContainer from 'views/molecules/notification/NotificationsContainer';
import Support from 'views/molecules/topbar/components/Support';
import Reflection from 'views/molecules/topbar/components/Reflection';
import UserContainer from 'views/molecules/topbar/components/UserContainer';
import Setting from 'views/molecules/topbar/components/Setting';
import {
  DATA_EXPORT,
  DASHBOARD,
  CAPI_SETTING,
  CHATBOT,
} from 'services/routes/constants';
import pages from 'services/routes/pages';
import PermissionWrapperRPC from 'services/appconfig/PermissionWrapperRPC';
import useCheckPermissionCapi from 'services/custom-hooks/useCheckPermissionCapi';
import useCheckPermissionAgency from 'services/custom-hooks/useCheckPermissionAgency';
import { getAccountId, getAiProfile, getUserId } from 'store/auth/selectors';

import { dataExportPermissionsAccess } from 'domain/permissions/screenPermissionConfigs';

import 'views/molecules/topbar/topbar.scss';
import SettingSingleCapi from './components/SettingSingleCapi';

function chatbotIntroPopperRepositionEffect({ instance, state }) {
  const safeUpdate = () => {
    try {
      if (instance) instance.update();
    } catch {
      /* instance destroyed */
    }
  };

  const roList = [];
  const observe = (el) => {
    if (!(el instanceof Element)) return;
    const ro = new ResizeObserver(safeUpdate);
    ro.observe(el);
    roList.push(ro);
  };

  const { reference } = state.elements;
  observe(reference);

  const topBar = document.getElementById('top-bar');
  observe(topBar);

  const navIcon =
    reference && reference.closest && reference.closest('.navbar-nav-icon');
  if (navIcon) observe(navIcon);

  const timeouts = [80, 200, 450, 900].map((ms) =>
    window.setTimeout(safeUpdate, ms)
  );

  let rafId;
  let n = 0;
  const tick = () => {
    safeUpdate();
    n += 1;
    if (n < 30) rafId = window.requestAnimationFrame(tick);
  };
  rafId = window.requestAnimationFrame(tick);

  return () => {
    roList.forEach((ro) => ro.disconnect());
    timeouts.forEach(clearTimeout);
    if (rafId != null) window.cancelAnimationFrame(rafId);
  };
}

/** Delay before first-time icon state; unchanged product behavior. */
const CHATBOT_INTRO_INITIAL_DELAY_MS = 2000;
/**
 * Wait after icon gets `.is-first-time` before showing the popover.
 * Match longest `transition` on `.chatbot-navigate-button img` in topbar.scss (transform 0.5s).
 */
const CHATBOT_INTRO_ICON_ANIMATION_MS = 500;

const CHATBOT_INTRO_POPPER_CONFIG = {
  strategy: 'fixed',
  modifiers: [
    {
      name: 'offset',
      options: {
        offset: [0, 12],
      },
    },
    {
      name: 'chatbotIntroRepositionOnlySelf',
      enabled: true,
      phase: 'afterWrite',
      fn: () => {},
      effect: chatbotIntroPopperRepositionEffect,
    },
  ],
};

/** Tooltip is portaled to body; fixed + offset aligns with sticky top-bar better than margin hacks. */
const CHATBOT_ASSIST_TOOLTIP_POPPER_CONFIG = {
  strategy: 'fixed',
  modifiers: [
    {
      name: 'offset',
      options: {
        offset: [0, 8],
      },
    },
  ],
};

const renderForCapiContractSingle = () => {
  return (
    <>
      <Nav variant="pills" className="mr-auto">
        <Link to={`/${CAPI_SETTING}`} className="logo">
          <img height="40" src={capiSingleLogo} alt="AD Ebis Logo" />
        </Link>
      </Nav>
      <SettingSingleCapi />
    </>
  );
};

function Topbar({ isShowNavbar, toggleNavbar, screenId }) {
  // eslint-disable-next-line no-unused-vars
  const { t } = useTranslation();

  const wrapper = classNames('top-bar');

  const { isCapiSingleContract } = useCheckPermissionCapi();
  const { isAgentUser } = useCheckPermissionAgency();
  const {aiOptout, aiChatFlag} = useSelector(getAiProfile, shallowEqual);
  const accountId = useSelector(getAccountId, shallowEqual);
  const userId = useSelector(getUserId, shallowEqual);

  const introStorageKey = useMemo(() => {
    if (accountId == null || userId == null) return null;
    const prefix = process.env.REACT_APP_LOCAL_STORAGE_KEY || 'redjug';
    return `${prefix}.aiAssistTopbarIntroDismissed.${String(
      accountId
    )}.${String(userId)}`;
  }, [accountId, userId]);

  /** Once dismissed (this session or localStorage), popover never shows again. */
  const [introDone, setIntroDone] = useState(false);
  /** After initial delay, icon gets first-time styles (CSS transition). */
  const [introIconRevealed, setIntroIconRevealed] = useState(false);
  /** Shown only after intro icon animation finishes (see CHATBOT_INTRO_ICON_ANIMATION_MS). */
  const [introPopoverVisible, setIntroPopoverVisible] = useState(false);
  const introRevealTimerRef = useRef(null);
  const introPopoverTimerRef = useRef(null);

  useEffect(() => {
    if (introRevealTimerRef.current != null) {
      window.clearTimeout(introRevealTimerRef.current);
      introRevealTimerRef.current = null;
    }
    if (introPopoverTimerRef.current != null) {
      window.clearTimeout(introPopoverTimerRef.current);
      introPopoverTimerRef.current = null;
    }
    if (!introStorageKey) return undefined;
    const aiAssistAfterLoginState =
      localStorage.getItem('ai_assist_after_login_state') === '1';

    let dismissed = false;
    try {
      dismissed = window.localStorage.getItem(introStorageKey) === '1';
    } catch {
      dismissed = false;
    }

    if (dismissed || !aiAssistAfterLoginState) {
      setIntroDone(true);
      setIntroIconRevealed(false);
      setIntroPopoverVisible(false);
      return undefined;
    }

    setIntroDone(false);
    introRevealTimerRef.current = window.setTimeout(() => {
      introRevealTimerRef.current = null;
      setIntroIconRevealed(true);
      introPopoverTimerRef.current = window.setTimeout(() => {
        introPopoverTimerRef.current = null;
        setIntroPopoverVisible(true);
      }, CHATBOT_INTRO_ICON_ANIMATION_MS);
    }, CHATBOT_INTRO_INITIAL_DELAY_MS);
    localStorage.removeItem('ai_assist_after_login_state');
    return () => {
      if (introRevealTimerRef.current != null) {
        window.clearTimeout(introRevealTimerRef.current);
        introRevealTimerRef.current = null;
      }
      if (introPopoverTimerRef.current != null) {
        window.clearTimeout(introPopoverTimerRef.current);
        introPopoverTimerRef.current = null;
      }
    };
  }, [introStorageKey]);

  const dismissChatbotIntro = useCallback(() => {
    if (introRevealTimerRef.current != null) {
      window.clearTimeout(introRevealTimerRef.current);
      introRevealTimerRef.current = null;
    }
    if (introPopoverTimerRef.current != null) {
      window.clearTimeout(introPopoverTimerRef.current);
      introPopoverTimerRef.current = null;
    }
    if (introStorageKey) {
      try {
        localStorage.setItem(introStorageKey, '1');
      } catch {
        /* ignore quota / private mode */
      }
    }
    setIntroDone(true);
    setIntroIconRevealed(false);
    setIntroPopoverVisible(false);
    // Popover close can leave focus on the link; focus + OverlayTrigger opens tooltip
    // and a hidden .tooltip can still sit in the DOM with opacity:0 but capture clicks.
    requestAnimationFrame(() => {
      document.getElementById('ai-chat-button')?.blur();
    });
  }, [introStorageKey]);

  const chatbotNavLink = (
    <Link
      to={pages[CHATBOT].path}
      target="_blank"
      rel="noopener noreferrer"
      className={`chatbot-navigate-button ${
        introIconRevealed && !introDone ? 'is-first-time' : ''
      }`}
      onClick={() => {
        if (!introDone) dismissChatbotIntro();
      }}
    >
      <img
        src={chatbotButtonImg}
        alt="Chatbot Button"
        className="chatbot-button-img"
        id="ai-chat-button"
      />
      <img
        src={chatbotButtonImgActive}
        alt="Chatbot Button Active"
        className="chatbot-button-img-active"
        id="ai-chat-button"
      />
      <img
        src={chatbotButtonStarActive}
        alt="Chatbot Button Active"
        className="chatbot-button-star-icon"
        id="ai-chat-button"
      />
    </Link>
  );

  return (
    <div id="top-bar" className={wrapper} screen-name={screenId}>
      <Navbar collapseOnSelect expand variant="light" bg="white">
        {isCapiSingleContract ? (
          renderForCapiContractSingle()
        ) : (
          <>
            <Nav variant="pills" className="mr-auto">
              <OverlayTrigger
                placement="bottom"
                overlay={
                  <Tooltip className="tooltip-common">
                    {!isShowNavbar ? 'サイドナビを開く' : 'サイドナビを閉じる'}
                  </Tooltip>
                }
              >
                <span
                  className={`d-flex align-items-center justify-content-center btn-icon-menu ${
                    isShowNavbar ? '' : 'is-hide'
                  }`}
                  onClick={() => toggleNavbar(!isShowNavbar)}
                  aria-hidden="true"
                >
                  <i className="fas fa-arrow-left" />
                  <i className="fas fa-bars" />
                </span>
              </OverlayTrigger>
              <Link to={`/${DASHBOARD}`} className="logo">
                <img height="40" src={ebisLogo} alt="AD Ebis Logo" />
              </Link>
            </Nav>

            {screenId !== CHATBOT && (
              <>
                <Setting />
                <PermissionWrapperRPC
                  requiredPermissions={dataExportPermissionsAccess}
                  resolveType="anyOf"
                  render={(isAllowed) => (
                    <Nav
                      className={classNames({
                        'navbar-nav-custom': true,
                        disabled: !isAllowed,
                        actived: screenId === DATA_EXPORT,
                      })}
                    >
                      <div className="nav-item">
                        <Link
                          to={`/${DATA_EXPORT}`}
                          className={classNames({
                            'nav-link': true,
                            'ebis--disabled': !isAllowed,
                          })}
                        >
                          データエクスポート
                        </Link>
                      </div>
                    </Nav>
                  )}
                />
              </>
            )}
          </>
        )}
        {screenId !== CHATBOT && (
          <Nav className="navbar-nav-icon">
            {!isCapiSingleContract && <Reflection />}
            {!isAgentUser && !aiOptout && aiChatFlag && (
              <span className="chatbot-nav-wrap d-inline-flex align-items-center">
                {!introDone ? (
                  <OverlayTrigger
                    key="chatbot-intro-overlay"
                    placement="bottom"
                    show={introPopoverVisible}
                    trigger={[]}
                    rootClose
                    transition={false}
                    popperConfig={CHATBOT_INTRO_POPPER_CONFIG}
                    onHide={dismissChatbotIntro}
                    overlay={
                      <Popover
                        id="chatbot-first-time-popover"
                        className="chatbot-first-time-popover"
                      >
                        <Popover.Content>
                          <div className="chatbot-first-time-popover__inner">
                            <span className="chatbot-first-time-popover__text">
                              ここからAIを使って分析データの検索ができます。
                              <br />
                              新機能をぜひお試しください！
                            </span>
                            <button
                              type="button"
                              className="chatbot-first-time-popover__close"
                              onClick={dismissChatbotIntro}
                              aria-label="閉じる"
                              id="ai-chat-notice-close"
                            >
                              <span aria-hidden="true">&times;</span>
                            </button>
                          </div>
                        </Popover.Content>
                      </Popover>
                    }
                  >
                    {chatbotNavLink}
                  </OverlayTrigger>
                ) : (
                  <OverlayTrigger
                    key="chatbot-assist-tooltip"
                    placement="bottom"
                    trigger={['hover']}
                    delay={{ show: 80, hide: 0 }}
                    popperConfig={CHATBOT_ASSIST_TOOLTIP_POPPER_CONFIG}
                    overlay={
                      <Tooltip className="tooltip-common ai-assist-topbar-tooltip">
                        AIアシスト（ベータ版）
                      </Tooltip>
                    }
                  >
                    {chatbotNavLink}
                  </OverlayTrigger>
                )}
              </span>
            )}
            <Support />
            <NotificationsContainer />
          </Nav>
        )}
        <Nav className="navbar-user">
          <UserContainer />
        </Nav>
      </Navbar>
    </div>
  );
}

Topbar.defaultProps = {
  isShowNavbar: true,
  toggleNavbar: () => {},
};

Topbar.propTypes = {
  isShowNavbar: PropTypes.bool,
  toggleNavbar: PropTypes.func,
  screenId: PropTypes.string.isRequired,
};

export default Topbar;
